import { useEffect, useState } from "react";
import { MapPin, Phone, Navigation, Building2, User } from "lucide-react";
import api from "@/lib/api";

/**
 * Customer-facing card showing the assigned Branch + Partner locations for a
 * given lead. Only renders content when at least one of the two is present.
 */
export default function AssignedLocationCard({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    api.get(`/customer/leads/${leadId}/assigned-location`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return null;
  if (!data || (!data.branch && !data.partner)) return null;

  return (
    <div className="card-surface p-6 space-y-4" data-testid={`assigned-loc-${leadId}`}>
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center">
          <MapPin className="w-4 h-4"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-500 font-bold">Your Assigned</div>
          <div className="font-heading text-white text-base leading-tight">Branch &amp; Partner Location</div>
        </div>
      </div>

      {data.branch && (
        <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4" data-testid="assigned-branch-block">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-cyan-400"/>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold">Shakthi Branch Office</div>
          </div>
          <div className="text-white font-semibold">{data.branch.branch_name} <span className="text-slate-500 font-normal">· {data.branch.district_name}</span></div>
          {data.branch.branch_address && <div className="text-xs text-slate-400 mt-1">{data.branch.branch_address}</div>}
          {(data.branch.branch_lat && data.branch.branch_lng) ? (
            <>
              <iframe title="Branch location" className="w-full h-40 rounded-lg mt-3 border border-white/10" src={`https://maps.google.com/maps?q=${data.branch.branch_lat},${data.branch.branch_lng}&z=15&output=embed`} loading="lazy"/>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <a data-testid="view-branch-map" target="_blank" rel="noreferrer" href={data.branch.branch_gmap_link || `https://maps.google.com/?q=${data.branch.branch_lat},${data.branch.branch_lng}`} className="h-9 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-xs flex items-center justify-center gap-1"><MapPin className="w-3.5 h-3.5"/>View Branch</a>
                <a data-testid="branch-directions" target="_blank" rel="noreferrer" href={`https://maps.google.com/?daddr=${data.branch.branch_lat},${data.branch.branch_lng}`} className="h-9 rounded-full bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center gap-1"><Navigation className="w-3.5 h-3.5"/>Get Directions</a>
              </div>
            </>
          ) : (
            <div className="text-[11px] text-slate-500 mt-2 italic">GPS coming soon — Ask admin to pin this branch.</div>
          )}
        </div>
      )}

      {data.partner ? (
        <div className="rounded-xl border-2 border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4" data-testid="assigned-partner-block">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-[#D4AF37]"/>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Assigned Partner (DSA)</div>
            <div className="ml-auto text-[9px] font-mono-alt text-[#D4AF37]">{data.partner.partner_id || ""}</div>
          </div>
          <div className="flex gap-3">
            {data.partner.office_photo_b64 ? (
              <img src={data.partner.office_photo_b64} alt="office" className="w-16 h-16 rounded-lg object-cover border border-white/10"/>
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center text-slate-500 text-[10px]">No Photo</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold">{data.partner.name}</div>
              <div className="text-[11px] text-slate-400">{data.partner.office_name}</div>
              <div className="text-[10px] text-slate-500 mt-0.5 truncate">{data.partner.office_address}</div>
            </div>
          </div>
          {(data.partner.office_lat && data.partner.office_lng) && (
            <iframe title="Partner office" className="w-full h-40 rounded-lg mt-3 border border-white/10" src={`https://maps.google.com/maps?q=${data.partner.office_lat},${data.partner.office_lng}&z=16&output=embed`} loading="lazy"/>
          )}
          <div className="grid grid-cols-3 gap-2 mt-2">
            {(data.partner.office_lat && data.partner.office_lng) && (
              <>
                <a data-testid="view-partner-map" target="_blank" rel="noreferrer" href={data.partner.office_gmap_link || `https://maps.google.com/?q=${data.partner.office_lat},${data.partner.office_lng}`} className="h-9 rounded-full bg-[#D4AF37] hover:bg-[#e6c14a] text-slate-900 font-bold text-xs flex items-center justify-center gap-1"><MapPin className="w-3.5 h-3.5"/>View Office</a>
                <a data-testid="partner-directions" target="_blank" rel="noreferrer" href={`https://maps.google.com/?daddr=${data.partner.office_lat},${data.partner.office_lng}`} className="h-9 rounded-full bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center gap-1"><Navigation className="w-3.5 h-3.5"/>Directions</a>
              </>
            )}
            {data.partner.phone && (
              <a data-testid="call-partner" href={`tel:${data.partner.phone}`} className="h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-xs flex items-center justify-center gap-1 col-span-1"><Phone className="w-3.5 h-3.5"/>Call</a>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-4" data-testid="assigned-partner-none">
          <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Partner assignment</div>
          <div className="text-sm text-slate-300 mt-1">Your case is being routed. Your nearest branch is <b className="text-cyan-300">{data.branch?.branch_name || "—"}</b>. Contact admin for details.</div>
        </div>
      )}
    </div>
  );
}
